/**
 * Service for Trello API integration
 * Handles authentication, card creation, and tracking
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { TRELLO_CONFIG, getTrelloReturnUrl } from '../config/trello';
import {
  TrelloCard,
  TrelloCardRequest,
  TrelloTrackingDocument,
  TrelloTracking,
  SendToTrelloRequest,
  TrelloAuthState
} from '../types/Trello';
import { COLLECTION_NAMES } from '../utils/constants';
import { StorageUtil } from '../utils/storage';
import { TrelloCardFormatter } from '../utils/trelloCardFormatter';

export class TrelloService {
  private static instance: TrelloService;
  private authState: TrelloAuthState = {
    isAuthenticated: false,
    token: null
  };

  private constructor() {
    // Check for existing token in localStorage
    this.loadAuthState();

    // Check for token in URL hash (returning from Trello)
    this.checkUrlForToken();
  }

  static getInstance(): TrelloService {
    if (!TrelloService.instance) {
      TrelloService.instance = new TrelloService();
    }
    return TrelloService.instance;
  }

  /**
   * Load authentication state from localStorage
   */
  private loadAuthState(): void {
    const token = StorageUtil.getItem('trello_token');
    const userInfo = StorageUtil.getJSON<any>('trello_user');

    if (token) {
      this.authState = {
        isAuthenticated: true,
        token,
        userInfo
      };
    }
  }

  /**
   * Check URL hash for Trello token (when returning from authorization)
   */
  private checkUrlForToken(): void {
    try {
      const hash = window.location.hash;
      if (hash && hash.includes('token=')) {
        console.log('🔍 Found token in URL hash');
        const urlParams = new URLSearchParams(hash.substring(1));
        const token = urlParams.get('token');

        if (token) {
          console.log('✅ Token extracted from URL');
          this.authState = {
            isAuthenticated: true,
            token,
            userInfo: undefined
          };
          this.saveAuthState();

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

          // Get user info in background
          this.getUserInfo().catch(error => {
            console.log('Could not fetch user info:', error);
          });
        }
      }
    } catch (error) {
      console.error('Error checking URL for token:', error);
    }
  }

  /**
   * Save authentication state to localStorage
   */
  private saveAuthState(): void {
    if (this.authState.token) {
      StorageUtil.setItem('trello_token', this.authState.token);
      if (this.authState.userInfo) {
        StorageUtil.setJSON('trello_user', this.authState.userInfo);
      }
    } else {
      StorageUtil.removeItem('trello_token');
      StorageUtil.removeItem('trello_user');
    }
  }

  /**
   * Get current authentication state
   */
  getAuthState(): TrelloAuthState {
    return { ...this.authState };
  }

  /**
   * Authenticate with Trello using redirect flow
   */
  async authenticate(): Promise<boolean> {
    console.log('🔐 Starting Trello authentication...');

    // Use environment-aware return URL that matches Trello app configuration
    const returnUrl = encodeURIComponent(getTrelloReturnUrl());
    const authUrl = `https://trello.com/1/authorize?key=${TRELLO_CONFIG.API_KEY}&name=${encodeURIComponent(TRELLO_CONFIG.NAME)}&scope=${TRELLO_CONFIG.SCOPE}&expiration=${TRELLO_CONFIG.EXPIRATION}&response_type=token&return_url=${returnUrl}`;

    console.log('🔄 Redirecting to Trello for authentication...', authUrl);
    window.location.href = authUrl;

    // This will never resolve due to redirect, but return Promise for consistency
    return new Promise(() => {});
  }

  /**
   * Get user information from Trello
   */
  private async getUserInfo(): Promise<void> {
    if (!this.authState.token) return;

    try {
      const response = await fetch(`${TRELLO_CONFIG.BASE_URL}/members/me?key=${TRELLO_CONFIG.API_KEY}&token=${this.authState.token}`);

      if (response.ok) {
        const userInfo = await response.json();
        this.authState.userInfo = {
          id: userInfo.id,
          username: userInfo.username,
          fullName: userInfo.fullName
        };
        this.saveAuthState();
      }
    } catch (error) {
      console.error('Error getting Trello user info:', error);
    }
  }

  /**
   * Logout from Trello
   */
  logout(): void {
    this.authState = {
      isAuthenticated: false,
      token: null
    };
    this.saveAuthState();
  }

  /**
   * Create a card in Trello
   */
  async createCard(cardData: TrelloCardRequest): Promise<TrelloCard> {
    if (!this.authState.token) {
      throw new Error('Not authenticated with Trello');
    }

    const url = `${TRELLO_CONFIG.BASE_URL}/cards?key=${TRELLO_CONFIG.API_KEY}&token=${this.authState.token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cardData)
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to create Trello card: ${error}`);
    }

    return await response.json();
  }

  /**
   * Send a lead to Trello
   */
  async sendLeadToTrello(request: SendToTrelloRequest, listId: string): Promise<TrelloTracking> {
    try {
      // Check if lead was already sent
      const existingTracking = await this.getLeadTrelloTracking(request.leadId);
      if (existingTracking) {
        throw new Error('Lead has already been sent to Trello');
      }

      // Format card description
      const cardDescription = TrelloCardFormatter.formatLeadDescription(request.leadData);

      // Create Trello card
      const cardRequest: TrelloCardRequest = {
        name: request.leadData.companyName,
        desc: cardDescription,
        idList: listId,
        labels: [{
          name: 'Lead Gen UI',
          color: 'blue'
        }],
        pos: 'top'
      };

      const trelloCard = await this.createCard(cardRequest);

      // Save tracking info to Firebase
      const trackingDoc: Omit<TrelloTrackingDocument, 'id'> = {
        leadId: request.leadId,
        userUid: request.userUid,
        trelloCardId: trelloCard.id,
        trelloCardUrl: trelloCard.shortUrl,
        sentAt: new Date().toISOString(),
        cardName: trelloCard.name,
        success: true
      };

      // Save to Firestore
      const trackingRef = collection(db, COLLECTION_NAMES.TRELLO_TRACKING);
      const docRef = await addDoc(trackingRef, trackingDoc);

      console.log(`✅ Lead ${request.leadId} sent to Trello successfully`);

      return {
        leadId: request.leadId,
        trelloCardId: trelloCard.id,
        trelloCardUrl: trelloCard.shortUrl,
        sentAt: trackingDoc.sentAt,
        sentBy: this.authState.userInfo?.fullName || this.authState.userInfo?.username || 'Unknown',
        trackingId: docRef.id
      };

    } catch (error) {
      console.error('❌ Error sending lead to Trello:', error);

      // Save error tracking to Firebase
      try {
        const errorDoc: Omit<TrelloTrackingDocument, 'id'> = {
          leadId: request.leadId,
          userUid: request.userUid,
          trelloCardId: '',
          trelloCardUrl: '',
          sentAt: new Date().toISOString(),
          cardName: request.leadData.companyName,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };

        // Save error to Firestore
        const trackingRef = collection(db, COLLECTION_NAMES.TRELLO_TRACKING);
        await addDoc(trackingRef, errorDoc);
      } catch (dbError) {
        console.error('Failed to save error tracking:', dbError);
      }

      throw error;
    }
  }


  /**
   * Get Trello tracking for a specific lead
   */
  async getLeadTrelloTracking(leadId: string): Promise<TrelloTracking | null> {
    try {
      const trackingRef = collection(db, COLLECTION_NAMES.TRELLO_TRACKING);
      const q = query(
        trackingRef,
        where('leadId', '==', leadId),
        where('success', '==', true),
        orderBy('sentAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data() as TrelloTrackingDocument;

      return {
        leadId: data.leadId,
        trelloCardId: data.trelloCardId,
        trelloCardUrl: data.trelloCardUrl,
        sentAt: data.sentAt,
        sentBy: 'Unknown', // We'll need to enhance this with user lookup
        trackingId: doc.id
      };

    } catch (error) {
      console.error('❌ Error getting Trello tracking:', error);
      return null;
    }
  }

  /**
   * Check if a lead has been sent to Trello
   */
  async isLeadSentToTrello(leadId: string): Promise<boolean> {
    const tracking = await this.getLeadTrelloTracking(leadId);
    return tracking !== null;
  }
}

// Export singleton instance
export const trelloService = TrelloService.getInstance();